// src/components/common/SyncStatusToast.tsx
// MUI Snackbar-based toasts for individual sync events.
// Shows success/failure/conflict messages with action buttons.

import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, AlertColor, Button } from '@mui/material';
import { useOffline } from '../../hooks/useOffline';
import { SyncEvent } from '../../services/syncEngine';
import translations, { Language } from '../../translations';

const getT = (key: string, replacements?: Record<string, string>): string => {
  const lang = (localStorage.getItem('appLanguage') || 'ar') as Language;
  let text = translations[lang]?.[key] || translations['ar']?.[key] || key;
  if (replacements) {
    for (const [k, v] of Object.entries(replacements)) {
      text = text.replace(`{{${k}}}`, v);
    }
  }
  return text;
};

interface ToastMessage {
  id: number;
  severity: AlertColor;
  message: string;
  action?: { label: string; onClick: () => void };
}

let toastIdCounter = 0;

const SyncStatusToast: React.FC = () => {
  const { lastSyncEvent, retryFailed } = useOffline();
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [currentToast, setCurrentToast] = useState<ToastMessage | null>(null);
  const [open, setOpen] = useState(false);

  const addToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const newToast = { ...toast, id: ++toastIdCounter };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  // Process sync events into toasts
  useEffect(() => {
    if (!lastSyncEvent) return;

    const event = lastSyncEvent;

    switch (event.type) {
      case 'sync:allCompleted': {
        const summary = event.summary;
        if (summary && summary.total > 0) {
          if (summary.failed === 0) {
            addToast({
              severity: 'success',
              message: getT('offline.sync.completed', { count: String(summary.succeeded) }),
            });
          } else {
            addToast({
              severity: 'warning',
              message: getT('offline.sync.partialFail', {
                failed: String(summary.failed),
                total: String(summary.total),
              }),
              action: {
                label: getT('offline.banner.retry'),
                onClick: () => retryFailed(),
              },
            });
          }
        }
        break;
      }

      case 'sync:conflict': {
        const entityType = event.operation?.entityType || 'entity';
        addToast({
          severity: 'info',
          message: getT('offline.sync.conflict', { entity: entityType }),
        });
        break;
      }

      // Individual failures are aggregated in allCompleted, no per-op toasts needed
      default:
        break;
    }
  }, [lastSyncEvent, addToast, retryFailed]);

  // Show toasts one at a time from the queue
  useEffect(() => {
    if (!currentToast && toasts.length > 0) {
      const [next, ...rest] = toasts;
      setCurrentToast(next);
      setToasts(rest);
      setOpen(true);
    }
  }, [currentToast, toasts]);

  const handleClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setCurrentToast(null);
  };

  if (!currentToast) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={5000}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: 1 }}
    >
      <Alert
        onClose={handleClose}
        severity={currentToast.severity}
        variant="filled"
        sx={{
          width: '100%',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          fontWeight: 500,
          '& .MuiAlert-icon': { fontSize: 22 },
        }}
        action={
          currentToast.action ? (
            <Button
              color="inherit"
              size="small"
              onClick={currentToast.action.onClick}
              sx={{ fontWeight: 600, fontSize: '0.8rem' }}
            >
              {currentToast.action.label}
            </Button>
          ) : undefined
        }
      >
        {currentToast.message}
      </Alert>
    </Snackbar>
  );
};

export default SyncStatusToast;
